import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { LogOut, ArrowLeft, Search, Calendar, User, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const RHOffresArchivees = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [archivedOffers, setArchivedOffers] = useState([]);
  const [filteredOffers, setFilteredOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreOfferId, setRestoreOfferId] = useState(null);
  const [restorePassword, setRestorePassword] = useState('');

  useEffect(() => {
    fetchArchivedOffers();
  }, []);

  useEffect(() => {
    filterOffers();
  }, [archivedOffers, searchQuery]);

  const fetchArchivedOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers?status=archive`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const offers = await response.json();
        // Filter out Horizon Solution for RC pages (this is RH page so we include all)
        setArchivedOffers(offers);
      } else {
        toast.error('Erreur lors du chargement des offres archivées');
      }
    } catch (error) {
      console.error('Error fetching archived offers:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const filterOffers = () => {
    if (!searchQuery.trim()) {
      setFilteredOffers(archivedOffers);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = archivedOffers.filter(offer =>
      offer.title.toLowerCase().includes(query) ||
      offer.company.toLowerCase().includes(query) ||
      (offer.creator_name && offer.creator_name.toLowerCase().includes(query))
    );
    setFilteredOffers(filtered);
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleBackToRH = () => {
    navigate('/rh');
  };

  const handleOfferClick = (offer) => {
    navigate(`/rh/archived-offer-detail/${offer.id}`);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canBeRestored = (archivedAt) => {
    if (!archivedAt) return false;
    const archived = new Date(archivedAt);
    const now = new Date();
    const diffHours = (now - archived) / (1000 * 60 * 60);
    return diffHours < 24;
  };

  const handleRestoreClick = (offerId, e) => {
    e.stopPropagation(); // Prevent card click
    setRestoreOfferId(offerId);
    setShowRestoreModal(true);
  };

  const handleRestoreOffer = async () => {
    if (!restorePassword.trim()) {
      toast.error('Veuillez saisir votre mot de passe');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers/${restoreOfferId}/restore`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: restorePassword
        }),
      });

      if (response.ok) {
        toast.success('Offre restaurée avec succès');
        setShowRestoreModal(false);
        setRestorePassword('');
        setRestoreOfferId(null);
        // Refresh the list
        fetchArchivedOffers();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors de la restauration');
      }
    } catch (error) {
      console.error('Error restoring offer:', error);
      toast.error('Erreur de connexion');
    }
  };

  // Company logos
  const companyLogos = {
    'Ma Conciergerie': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/mruq8p60_PNG_MA%20CONCIERGERIE.png',
    'Mon Evenementiel': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/o94kh96a_logo-monevenemenciel_png.png',
    'SISENS': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/gwqu631h_25.04.10%20SISENS%20LOGO.png',
    'Prysme': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/nzxlkuw3_logo-prysme-couleur.png'
  };

  return (
    <div className="rh-offres-archivees-page">
      {/* Header */}
      <header className="rh-header">
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleBackToRH}
            variant="outline"
            size="sm"
            className="btn-outline"
            style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour
          </Button>
          <img
            src="https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/nzxlkuw3_logo-prysme-couleur.png"
            alt="Logo Prysme"
            className="rh-header-logo"
          />
        </div>
        <h1>Offres archivées</h1>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="rh-main">
        {/* Search Bar */}
        <div className="search-section">
          <div className="search-bar">
            <Search size={20} className="search-icon" />
            <Input
              type="text"
              placeholder="Rechercher par intitulé, entreprise ou manager..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          <div className="search-counter">
            {filteredOffers.length} offre{filteredOffers.length !== 1 ? 's' : ''} archivée{filteredOffers.length !== 1 ? 's' : ''}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="recruitment-grid-compact">
            {filteredOffers.map((offer) => (
              <Card 
                key={offer.id}
                className="recruitment-card-compact cursor-pointer group"
                onClick={() => handleOfferClick(offer)}
              >
                <CardContent className="recruitment-card-compact-content">
                  <div className="recruitment-card-compact-header">
                    <img
                      src={companyLogos[offer.company]}
                      alt={`Logo ${offer.company}`}
                      className="recruitment-card-compact-logo"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <Eye className="recruitment-card-compact-icon" />
                  </div>
                  <h3 className="recruitment-card-compact-title">
                    {offer.company} : {offer.title}
                  </h3>
                  <div className="recruitment-card-compact-info">
                    <div className="recruitment-card-compact-info-item">
                      <User size={14} />
                      <span><strong>Manager :</strong> {offer.creator_name || 'Non spécifié'}</span>
                    </div>
                    <div className="recruitment-card-compact-info-item">
                      <Calendar size={14} />
                      <span><strong>Date idéale :</strong> {formatDate(offer.date_arrivee)}</span>
                    </div>
                    <div className="recruitment-card-compact-info-item archive-date-item">
                      <Calendar size={14} />
                      <span><strong>Archivée le :</strong> {formatDateTime(offer.archived_at)}</span>
                    </div>
                  </div>
                  {canBeRestored(offer.archived_at) && (
                    <div className="recruitment-card-restore-section">
                      <Button
                        onClick={(e) => handleRestoreClick(offer.id, e)}
                        variant="outline"
                        size="sm"
                        className="restore-button"
                        style={{ borderColor: '#10b981', color: '#10b981' }}
                      >
                        Restaurer
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            {filteredOffers.length === 0 && !loading && (
              <div className="empty-state-compact">
                <p>
                  {searchQuery 
                    ? 'Aucune offre archivée ne correspond à votre recherche' 
                    : 'Aucune offre archivée'
                  }
                </p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Restore Modal */}
      {showRestoreModal && (
        <div className="validated-archive-modal">
          <div className="validated-archive-modal-content">
            <h3 className="validated-archive-title">Restaurer l'offre archivée</h3>
            <p className="validated-archive-text">
              Cette action remettra l'offre dans la section "Offres validées". Veuillez confirmer avec votre mot de passe RH.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe RH
              </label>
              <input
                type="password"
                value={restorePassword}
                onChange={(e) => setRestorePassword(e.target.value)}
                className="validated-archive-input"
                placeholder="Saisissez votre mot de passe"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleRestoreOffer();
                  }
                }}
              />
            </div>
            <div className="validated-archive-actions">
              <button
                onClick={() => {
                  setShowRestoreModal(false);
                  setRestorePassword('');
                  setRestoreOfferId(null);
                }}
                className="validated-archive-btn validated-archive-btn-cancel"
              >
                Annuler
              </button>
              <button
                onClick={handleRestoreOffer}
                className="validated-archive-btn"
                style={{ background: '#10b981', borderColor: '#10b981', color: 'white' }}
              >
                Restaurer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RHOffresArchivees;